import type {SummaryNode as SummaryNodeType} from 'src/types/chatbot';
import type {NodeEditorProps} from './node-editor-v2';

import * as React from 'react';
import keyBy from 'lodash/keyBy';

import classify from 'src/utils/classify';

import {useChatbotStateContext} from '../chatbot-state-context';
import useI18n from 'src/hooks/useI18n';

import {
  createSummaryNode,
  createEditableGraph,
  findAncestorNodeVariables,
  nodeToSummaryItem,
  getAncestorQuestionNodes,
} from '../utils';

import {useMutableFieldStatus} from '../api-hooks';

import {validateNode} from '../node-editor/validation';

import {Button} from 'src/designSystem2021Components/buttons';

import {Input} from 'src/designSystem2021Components/input';
import {Checkbox} from 'src/designSystem2021Components/checkbox';

import {useEditorState, useAtsVariables} from '../node-editor/reducer';

import css from './node-editor-v2.css';


export default function SummaryNode({
  graph,
  onCancel,
  readOnly,
  isSavingNode,
  setIsSavingNode,
}: NodeEditorProps) {
  const {labelI18nInstance} = useI18n();
  const t = labelI18nInstance.t;
  const {state, dispatch} = useChatbotStateContext();
  const [hasSubmitted, setHasSubmitted] = React.useState(false);

  const editableGraph = createEditableGraph(graph);
  const editingNodeData = graph.nodes.find(({id}) => id === state.editingNode);
  const edgeId = Number(state.insertingNode?.edgeId || state.editingNode);

  const defaultNode =
    state.editingNode !== null && editingNodeData?.type === 'summary'
      ? editingNodeData
      : createSummaryNode(
          editableGraph,
          edgeId ? editableGraph.edges.get(edgeId) : undefined,
        );

  const {isFieldReadOnly, canFlowUpdate} = useMutableFieldStatus(
    readOnly,
    defaultNode.type,
  );

  const [editorState, editorDispatch] = useEditorState<SummaryNodeType>({
    defaultNode,
  });

  const {node} = editorState;
  const [summaryItems, setSummmaryItems] = React.useState(node.summary_items);

  React.useEffect(() => {
    const edge = edgeId ? editableGraph.edges.get(edgeId) : null;
    const parentNodeId = edge ? edge.to_node_id ?? edge.from_node_id : null;
    const currentNode =
      edgeId != null && parentNodeId != null
        ? editableGraph.nodes.get(parentNodeId)
        : defaultNode;
    const questionNodes = getAncestorQuestionNodes(
      editableGraph,
      currentNode,
      state.insertingNode?.edgeId,
    );
    const summaryNodeOrder = questionNodes.map((node) => node.id).reverse();

    const existingSummaryIds = summaryItems.map(({node_id}) => node_id);
    const newQuestions = questionNodes
      .filter((node) => !existingSummaryIds.includes(node.id))
      .map(nodeToSummaryItem);

    const updatedSummaryItems = [...summaryItems, ...newQuestions];

    const filteredSummaryItems = updatedSummaryItems.filter(({node_id}) =>
      questionNodes.map(({id}) => id).includes(node_id),
    );

    const canSummaryItemsBeUpdated = isFieldReadOnly('summaryItems');

    const newSummaryItems =
      !canSummaryItemsBeUpdated && readOnly
        ? node.summary_items
        : filteredSummaryItems;
    const newSummaryItemsById = keyBy(newSummaryItems, 'node_id');

    const sortedSummaryItems = summaryNodeOrder.map(
      (id) => newSummaryItemsById[id],
    );

    setSummmaryItems(sortedSummaryItems);
    setNode({
      ...node,
      summary_items: sortedSummaryItems,
    });
  }, []);

  const setNode = (payload: SummaryNodeType) => {
    editorDispatch({type: 'update', payload});
  };

  const nodeVariables = findAncestorNodeVariables(
    editableGraph,
    defaultNode,
    edgeId,
  );
  const dynamicLabels = useAtsVariables(graph.anchor_entity_type);
  // @ts-expect-error - TS2554 - Expected 2 arguments, but got 1.
  const dynamicLabelMap = React.useMemo(() => keyBy(dynamicLabels, 'value'));
  const emailDynamicLabels = React.useMemo(
    () =>
      dynamicLabels.filter((label) => ['string', 'email'].includes(label.type)),
    [dynamicLabels],
  );

  const validation = validateNode(node, editableGraph);
  const error = hasSubmitted ? validation : null;

  const handleSaveNode = () => {
    if (validation) {
      setHasSubmitted(true);
    } else {
      state.insertingNode
        ? dispatch({
            type: 'COMMIT_NODE',
            payload: node,
          })
        : dispatch({
            type: 'UPDATE_NODE',
            payload: node,
          });
    }
  };

  React.useEffect(() => {
    if (isSavingNode) {
      handleSaveNode();
      setIsSavingNode(false);
    }
  }, [isSavingNode]);

  return (
    <div className={css.questionNodeContainer}>
      <div className={css.section}>
        <Input
          label={t('Node_Title', 'Node Title')}
          value={node.name}
          onChange={(event) =>
            setNode({...node, name: event.currentTarget.value})
          }
        />
        {error?.name && (
          <div className={css.errorText}>
            {error.name
              ? error.name.errors?.includes('EMPTY')
                ? `${t(
                    'This_node_must_have_a_name',
                    'This node must have a name',
                  )}.`
                : `${t(
                    'This_name_is_already_taken',
                    'This name is already taken',
                  )}.`
              : `${t(
                  'Node_names_must_be_unique_40_characters_maximum',
                  'Node names must be unique. 40 characters maximum.',
                )}.`}
          </div>
        )}
      </div>
      <div className={css.section}>
        <p className={css.description}>{`${t(
          'Below_is_a_list_of_all_the_question_nodes_in_the_conversation_flow_This_list_will_update_automatically_if_a_question_node_is_added_or_deleted_To_exclude_a_question_uncheck_it_in_the_list',
          'Below is a list of all the question nodes in the conversation flow. This list will update automatically if a question node is added or deleted. To exclude a question uncheck it in the list',
        )}.`}</p>
      </div>

      <div className={classify(css.section, css.padded)}>
        <div>
          <Checkbox
            className={css.checkbox}
            checked={
              node.summary_items.every(({enabled}) => enabled)
                ? 'true'
                : 'false'
            }
            onChange={(checked) => {
              const enabled = checked === 'true' ? true : false;

              setNode({
                ...node,
                summary_items: node.summary_items.map((item) => ({
                  ...item,
                  enabled,
                })),
              });
            }}
          />
          {t('Enable_all', 'Enable all')}
        </div>
        {node.summary_items.map(({enabled, node_id, short_name}, index) => {
          return (
            <div className={css.optionRow}>
              <Checkbox
                className={css.checkbox}
                checked={enabled ? 'true' : 'false'}
                onChange={(checked) => {
                  const itemEnabled = checked === 'true' ? true : false;
                  const newSummary = [...node.summary_items];
                  newSummary[index] = {
                    ...newSummary[index],
                    enabled: itemEnabled,
                  };
                  setNode({
                    ...node,
                    summary_items: newSummary,
                  });
                }}
              />
              <label className={css.inputContainer}>
                <Input
                  label={t('Node_Name', 'Node Name')}
                  value={editableGraph.nodes.get(node_id)?.name}
                  disabled
                />
              </label>

              <label className={css.inputContainer}>
                <Input
                  label={t('Node_Label', 'Node Label')}
                  value={short_name}
                  onChange={(e) => {
                    const newSummary = [...node.summary_items];
                    newSummary[index] = {
                      ...newSummary[index],
                      short_name: e.currentTarget.value,
                    };
                    setNode({
                      ...node,
                      summary_items: newSummary,
                    });
                  }}
                />
              </label>
            </div>
          );
        })}
      </div>

      <div className={css.footer}>
        <Button size="small" type="secondary" onClick={onCancel}>
          {t('Cancel', 'Cancel')}
        </Button>
        <Button
          size="small"
          type="primary"
          onClick={handleSaveNode}
          disabled={hasSubmitted && !!validation}
        >
          {canFlowUpdate ? t('Update', 'Update') : t('Save', 'Save')}
        </Button>
      </div>
    </div>
  );
}
